import styled from '@emotion/styled';
import React from 'react';
import { useTranslation } from 'react-i18next';
import {
    ContentBanner,
    Button,
    Icon,
    pxToRem,
    ContentBannerProps,
} from '@modernhealth/vital-components';

import { colors } from '@modernhealth/vital-foundation';

import { navigationService } from 'src/config/rootService';
import { ScheduledContentFragment } from 'src/modules/dashboard/documents/getHomeScreen.generated';
import { calculateDailyPauseStreakCTA } from '../util/calculateDailyPauseStreakCTA';
import { useAnalytics } from '@modernhealth/web-analytics';

export interface DailyPauseCardProps {
    scheduledContent: ScheduledContentFragment;
    dayStreakCount: number;
    weekStreakCount: number;
    showStreakInfo?: boolean;
}

export const DailyPauseCard: React.FC<DailyPauseCardProps> = ({
    scheduledContent,
    dayStreakCount,
    weekStreakCount,
    showStreakInfo,
}) => {
    const { t } = useTranslation();
    const analytics = useAnalytics();
    const { bannerTitle, bannerContent, bannerImage, uuid, accentColor, isCompleted, titleColor } =
        scheduledContent;

    const handleDailyPauseOpen = () => {
        navigationService.navigate('/pause', { uuid });
        analytics.trackClick('homepage: hero: daily pause', {
            scheduled_content_uuid: uuid,
            home_section: 'hero',
        });
    };

    const dailyPauseStreakCTA = calculateDailyPauseStreakCTA(dayStreakCount, weekStreakCount, t);

    const ctaButtonTitle = isCompleted
        ? t('dailyPause:content_banner.completed_button_text')
        : t('dailyPause:content_banner.button_text');

    const renderStreakInfo = () => (
        <StreakCta
            variant="outline"
            onClick={() => {
                navigationService.navigate('/pause/stats');
                analytics.trackClick('homepage: hero: daily pause streaks', {
                    home_section: 'hero',
                    cta_copy: dailyPauseStreakCTA,
                });
            }}
        >
            🔥 {dailyPauseStreakCTA} <Icon name="chevron-right" />
        </StreakCta>
    );

    return (
        <StyledContentBanner
            title={bannerTitle}
            description={bannerContent}
            image={bannerImage ?? undefined}
            buttonTitle={ctaButtonTitle}
            onClick={handleDailyPauseOpen}
            backgroundColor={accentColor ? `#${accentColor}` : colors.white}
            buttonProps={{
                icon: isCompleted ? 'checkmark' : undefined,
                variant: isCompleted ? 'flat' : 'secondary',
            }}
            footer={showStreakInfo ? renderStreakInfo() : null}
            titleColor={titleColor ?? undefined}
        />
    );
};

const StreakCta = styled(Button)({
    border: 'none',
    color: colors.white,
    paddingLeft: 0,
    marginTop: pxToRem(24),
});

// ContentBanner has theme props which is string and it conflicts with emotion theme props
const StyledContentBanner = styled(ContentBanner)<
    ContentBannerProps & { titleColor?: string },
    // @ts-ignore-next-line
    string
>(({ titleColor }) => {
    const color = titleColor ? `#${titleColor}` : undefined;

    return {
        h2: { color },
        h4: { color },
        p: { color },
    };
});
